import * as React from 'react';
import * as Classes from '../common/classes';
import classNames from 'classnames';
import { IBaseTextProps } from '../typography/Text';
import { useInverseBg } from '../theme/useInverseBg';
import { FormGroup } from '../form-group/FormGroup';
import { useContext } from 'react';
import { LabelValueListContext } from './LabelValueList';
import { Label } from '../label/Label';

const EMPTY_VALUE = '-';

export interface ILabelValueProps extends IBaseTextProps {
  /**
   * Inverzní verze - typicky používáme na tmavším pozadí.
   */
  inverse?: boolean;
  /**
   * Pokud není uveden, vykreslí se pouze hodnota - někdy je label zbytečný a je vhodné použít tuto komponentu už kvůli stylu hodnoty.
   */
  label?: React.ReactNode;
  /**
   * Pokud title není uvedeno, zobrazuje se po najetí na hodnotu text z label.
   * Pokud title je uvedeno, zobrazuje se po najetí jak na label, tak na hodnotu.
   */
  title?: string;
  /**
   * Jen pro variantu mimo LabelValueList.
   */
  labelClassName?: string;
  contentClassName?: string;
  /**
   * Jen pro variantu mimo LabelValueList.
   */
  horizontal?: boolean;
  /**
   * Co se má zobrazit při prázdné hodnotě? Ve vhochozím stavu se použije '-'.
   */
  emptyValue?: React.ReactNode;
  /**
   * Jen pro variantu mimo LabelValueList.
   */
  lastItem?: boolean;

  /**
   * Vycentruje horionztálně label, nefunguje pokud je horizontal true.
   */
  centeredLabel?: boolean;
  /**
   * Pro případ použití v label value list.
   */
  span?: number;
  /**
   * Pro případ použití v label value list.
   */
  alignRight?: boolean;
}

type LabelValueProps = React.PropsWithChildren<ILabelValueProps>;

type Props = LabelValueProps;

export const LabelValue: React.FC<Props> = ({
  inverse,
  label,
  title,
  children,
  style,
  intent,
  alignRight,
  italic,
  strong,
  className,
  underline,
  contentClassName,
  labelClassName,
  lastItem,
  horizontal = false,
  emptyValue,
  centeredLabel,
}) => {
  const listContext = useContext(LabelValueListContext);

  const parentInverse = useInverseBg(
    inverse === undefined ? listContext.inverse : inverse
  );

  let useIntent = intent === undefined ? listContext.intent : intent;
  let valueCls = classNames(contentClassName, Classes.LABEL_VALUE_CONTENT, {
    [Classes.TYPOGRAPHY_INHERIT]: !useIntent || useIntent === 'inherit',
    [Classes.TYPOGRAPHY_PRIMARY]: useIntent === 'primary',
    [Classes.TYPOGRAPHY_SECONDARY]: useIntent === 'secondary',
    [Classes.TYPOGRAPHY_DANGER]: useIntent === 'danger',
    [Classes.TYPOGRAPHY_SUCCESS]: useIntent === 'success',
    [Classes.TYPOGRAPHY_WARNING]: useIntent === 'warning',
    [Classes.TYPOGRAPHY_INACTIVE]: useIntent === 'inactive',
    [Classes.TYPOGRAPHY_DEFAULT]: useIntent === 'default',
    [Classes.TYPOGRAPHY_INVERSE]: parentInverse,
    [Classes.TYPOGRAPHY_STRONG]: strong,
    [Classes.TYPOGRAPHY_ITALIC]: italic,
    [Classes.TYPOGRAPHY_UNDERLINE]: underline,
  });

  let realValue;
  if (children === '' || children === null || children === undefined) {
    realValue = (
      <span
        title={title || (typeof label !== 'object' ? (label as string) : '')}
      >
        {emptyValue || listContext.emptyValue || EMPTY_VALUE}
      </span>
    );
  } else {
    realValue = (
      <span
        title={title || (typeof label !== 'object' ? (label as string) : '')}
      >
        {children}
      </span>
    );
  }

  if (listContext.active) {
    const labelIntent = useIntent ? listContext.intent : useIntent;

    const labelTitle =
      title === underline
        ? typeof label === 'string'
          ? label
          : underline
        : title;

    return (
      <>
        <th
          className={classNames(
            labelClassName,
            className,
            Classes.LABEL_VALUE_LIST_ITEM_LABEL
          )}
        >
          <Label inverse={inverse} intent={labelIntent}>
            <span title={title}>{label}</span>
          </Label>
        </th>
        <td
          className={classNames(
            valueCls,
            className,
            Classes.LABEL_VALUE_LIST_ITEM_VALUE,
            {
              [Classes.LABEL_VALUE_LIST_ITEM_VALUE_RIGHT]:
                alignRight === undefined ? listContext.alignRight : alignRight,
            }
          )}
          colSpan={listContext.valueSpan}
        >
          <div title={labelTitle}>{realValue}</div>
        </td>
      </>
    );
  } else {
    return (
      <FormGroup
        style={style}
        label={
          <span
            className={classNames(
              [Classes.LABEL],
              {
                [Classes.TYPOGRAPHY_INACTIVE]: useIntent === 'inactive',
                [Classes.TYPOGRAPHY_INVERSE]: parentInverse,
                [Classes.LABEL_VALUE_CENTRED]: centeredLabel && !horizontal,
              },
              labelClassName
            )}
            title={title}
          >
            {label}
          </span>
        }
        className={classNames(Classes.LABEL_VALUE, className, {
          [Classes.LABEL_VALUE_INLINE]: horizontal,
          [Classes.LABEL_VALUE_LAST_ITEM]: lastItem,
        })}
        contentClassName={valueCls}
        inline={horizontal}
      >
        {realValue}
      </FormGroup>
    );
  }
};
